import RenderManager from "../rendering/render-manager";
import AssetLibrary from "./asset-library";

function loadLight({ uuid, data, browserCapabilities }) {
    return new Promise(resolve => {
        let light = null;

        if (shouldIgnoreLight(data.type, browserCapabilities)) light = new Promise(resolve => resolve(null));
        else {
            switch (data.type) {
                case "AmbientLight":
                case "Ambient":
                    light = createAmbientLight(data);
                    break;
                case "HemisphereLight":
                case "Hemisphere":
                case "Hemi":
                    light = createHemisphereLight(data);
                    break;
                case "DirectionalLight":
                case "Directional":
                    light = createDirectionalLight(data);
                    break;
                case "SpotLight":
                case "Spot":
                    light = createSpotLight(data);
                    break;
                case "PointLight":
                case "Point":
                    light = createPointLight(data);
                    break;
                case "RectAreaLight":
                case "RectArea":
                case "Rect":
                    light = createRectAreaLight(data);
                    break;
            }
        }

        light.then(light => {
            if (light) {
                light.name = data.name;
                light.uuid = uuid;

                // Lights without shadows are lights that don"t need positionning,
                //      though they have pos/rot/scale properties (ambient, hemi).
                if (!!light.shadow) {
                    if (data.position) light.position.copy(data.position);
                    if (data.rotation) light.rotation.set(data.rotation.x, data.rotation.y, data.rotation.z, data.rotation.order);
                    if (data.quaternion) light.rotation.copy(data.quaternion);
                }

                addShadows(data, light);
                // addLensflares(data, light);
            }

            resolve({ uuid, light });
        });
    });
}

function finalizeLight({ uuid, light, objects, data, slides }) {
    if (!light) return;

    //parenting
    const parentUuid = data.assets.lights[uuid].parent;
    (parentUuid && objects[parentUuid] ? objects[parentUuid] : RenderManager.activeScene).add(light);

    const anim = slides[0].animation[uuid];

    light.visible = anim
        ? Boolean(anim.visible === undefined
            ? true
            : anim.visible)
        : false;

    let lightData = data.assets.lights[uuid];
    let targetUuid = lightData.targetUuid;
    if (targetUuid) {

        let target = objects[targetUuid];
        if (target) {
            light.target = target;
        } else {
            delete lightData.targetUuid;
            console.warn('Invalid light target: '+targetUuid);
        }

    }

}

export default loadLight;
export { loadLight, finalizeLight };

function shouldIgnoreLight(lightType, browserCapabilities) {
    switch (lightType) {
        case "AmbientLight":
        case "Ambient":
            return false;
        case "HemisphereLight":
        case "DirectionalLight":
        case "SpotLight":
        case "PointLight":
        case "RectAreaLight":
            return !Q3.browserCapabilities.supports.lights.advancedEnabled;
        default:
            return true;
    }
}

function getColor(data, key) {
    const color = data[key];

    return typeof (color) === "string"
        ? color.indexOf("#") > -1
            ? color
            : parseInt(color)
        : color;
}

function createAmbientLight(data) {
    const color = getColor(data, "color");
    const intensity = data.intensity;

    return new Promise(resolve => resolve(new THREE.AmbientLight(color, intensity)));
}

function createHemisphereLight(data) {
    const color = getColor(data, "color");
    const groundColor = getColor(data, "groundColor");
    const intensity = data.intensity;

    return new Promise(resolve => resolve(new THREE.HemisphereLight(color, groundColor, intensity)));
}

function createDirectionalLight(data) {
    const color = getColor(data, "color");
    const intensity = data.intensity;

    return new Promise(resolve => resolve(new THREE.DirectionalLight(color, intensity)));
}

function createSpotLight(data) {
    const color = getColor(data, "color");
    const intensity = data.intensity;
    const distance = data.distance;
    const angle = data.angle;
    const penumbra = data.penumbra;
    const decay = data.decay;

    return new Promise(resolve => resolve(new THREE.SpotLight(color, intensity, distance, angle, penumbra, decay)));
}

function createPointLight(data) {
    const color = getColor(data, "color");
    const intensity = data.intensity;
    const distance = data.distance;
    const decay = data.decay;

    return new Promise(resolve => resolve(new THREE.PointLight(color, intensity, distance, decay)));
}

function createRectAreaLight(data) {
    const color = getColor(data, "color");
    const intensity = data.intensity;
    const width = data.width;
    const height = data.height;

    return new Promise(resolve => resolve(new THREE.ReactAreaLight(color, intensity, width, height)));
}

function addShadows(data, light) {
    function hasShadows() {
        // RectAreaLight shadows are not working in ThreeJS yet.
        // PointLight shadows are too expensive so we decided to disable them

        return (!!light.shadow && data.type !== "RectAreaLight" && data.type !== "PointLight");
    }

    if (!hasShadows()) return;

    light.castShadow = !!data.castShadow;

    light.shadow.mapSize.set(1, 1).multiplyScalar(data.shadowResolution || 512);

    Object.assign(light.shadow.camera, {
        near: data.shadowNearPersp || 0.01,
        far: data.shadowFar || 1000
    });

    if (data.type === "DirectionalLight") {
        Object.assign(light.shadow.camera, {
            near: data.shadowNearOrtho || 0.01,
            left: -data.shadowWidth / 2 || -5,
            right: data.shadowWidth / 2 || 5,
            bottom: -data.shadowHeight / 2 || -5,
            top: data.shadowHeight / 2 || 5
        });
    }

    light.shadow.camera.updateProjectionMatrix();
    light.shadow.bias = data.shadowBias || 0;
    light.castShadow = !!data.castShadow;
}

function addLensflares(data, light) {
    if (data.lensflares) {
        const lensflares = createLensEffect();

        light.add(lensflares);

        light.userData.lensflares = lensflares;

        lensflares.lensFlares.forEach(flare => {
            flare.scale = data.lensflaresScale || 1;
        });

    }
}


function createLensEffect() {

    const flareColor = new THREE.Color(0xffffff);
    flareColor.setHSL(.9, .9, 1);

    const lensFlareUpdateCallback = object => {

        const vecX = -object.positionScreen.x * 2;
        const vecY = -object.positionScreen.y * 2;

        object.lensFlares.forEach(flare => {

            flare.x = object.positionScreen.x + vecX * flare.distance;
            flare.y = object.positionScreen.y + vecY * flare.distance;
            flare.rotation = 0;
        });

        object.lensFlares[2].y += 0.025;
        object.lensFlares[3].rotation = object.positionScreen.x * 0.5 + THREE.Math.degToRad(45);

    };

    const lensFlare = new THREE.LensFlare(AssetLibrary.textures.lensflare1, 1000, 0.0, THREE.AdditiveBlending, flareColor);

    lensFlare.add(AssetLibrary.textures.lensflare2, 2000, 0.0, THREE.AdditiveBlending);
    lensFlare.add(AssetLibrary.textures.lensflare3, 100, 0.2, THREE.AdditiveBlending);
    lensFlare.add(AssetLibrary.textures.lensflare3, 300, 0.3, THREE.AdditiveBlending);
    lensFlare.add(AssetLibrary.textures.lensflare3, 70, 0.5, THREE.AdditiveBlending);
    lensFlare.add(AssetLibrary.textures.lensflare3, 180, 0.9, THREE.AdditiveBlending);
    lensFlare.add(AssetLibrary.textures.lensflare3, 70, 1.0, THREE.AdditiveBlending);

    lensFlare.customUpdateCallback = lensFlareUpdateCallback;

    return lensFlare;

}